/**
 * AmenitiesEditor — edit mode for a property's amenities (SCRUM-141 / DS-010-5).
 *
 * Self-fetches the global catalog (`useAmenityCatalog`) and the property's
 * current amenities (`usePropertyAmenities`), seeds a draft (`selectedIds` +
 * `notes`) from the current assignment, and renders a searchable, grouped chip
 * picker (`AmenityCategoryGroup` per catalog group). Save issues the
 * full-replace PUT (`useSetPropertyAmenities`) with the draft items.
 *
 * Save discipline:
 *   - Items build as `{ amenityId }` (note omitted) or `{ amenityId, note }`
 *     where the note is `normalizeNote`d (trim / whitespace-only → null) so the
 *     optimistic cache equals the server-reconciled value.
 *   - Save is DISABLED while the mutation is pending (pending-save lock) OR
 *     while the draft fails the shared `setPropertyAmenitiesSchema` — the
 *     validators are the single source of truth (CLAUDE.md FE §4), never a
 *     local re-implementation.
 *   - On success → `onDone()` + a polite SR announcement.
 *   - On error → the mutation's typed `errorKind` drives a DISTINCT inline
 *     `role="alert"` message (unknown-amenity / conflict-closed / forbidden /
 *     not-found / other); rollback is handled inside the mutation.
 *
 * Cancel discards the draft and calls `onDone()` without mutating. When the
 * catalog is ready but empty, a no-catalog empty-state is shown instead of the
 * picker.
 */

import * as React from 'react';
import { Button, TextField } from '@sparta/ui-kit';
import {
  setPropertyAmenitiesSchema,
  type SetPropertyAmenitiesItem,
} from '@artemis/validators';
import { useAmenityCatalog } from '../../api/use-amenity-catalog.js';
import { usePropertyAmenities } from '../../api/use-property-amenities.js';
import {
  useSetPropertyAmenities,
  type SetAmenitiesErrorKind,
} from '../../api/use-set-property-amenities.js';
import { normalizeNote } from '../../api/amenity-note.js';
import { AmenityCategoryGroup } from './AmenityCategoryGroup.js';

export interface AmenitiesEditorProps {
  readonly propertyId: string;
  readonly onDone: () => void;
}

/** Distinct inline copy per documented PUT failure (codex HIGH-3). */
const ERROR_COPY: Readonly<Record<SetAmenitiesErrorKind, string>> = {
  'unknown-amenity':
    'One of the selected amenities is no longer available. Refresh and try again.',
  'conflict-closed':
    'This organization is closed, so amenities cannot be changed.',
  forbidden: 'You do not have permission to change amenities for this property.',
  'not-found': 'This property was not found. It may have been removed.',
  other: 'Something went wrong while saving amenities. Please try again.',
};

function buildItems(
  selectedIds: ReadonlySet<string>,
  notes: ReadonlyMap<string, string>,
): SetPropertyAmenitiesItem[] {
  // Every item carries an explicit `note` (`string | null`): a real note stays
  // as its `normalizeNote`d value; an empty/whitespace-only note becomes `null`
  // (mirrors the BE `AmenityNote` VO, so optimistic == reconciled). The shared
  // `setPropertyAmenitiesItemSchema` accepts `note: null` (nullable+optional),
  // and the wire full-replace treats `null` as "no note".
  return [...selectedIds].map((amenityId) => ({
    amenityId,
    note: normalizeNote(notes.get(amenityId)),
  }));
}

export function AmenitiesEditor({
  propertyId,
  onDone,
}: AmenitiesEditorProps): React.ReactElement {
  const catalog = useAmenityCatalog();
  const current = usePropertyAmenities(propertyId);
  const setAmenities = useSetPropertyAmenities(propertyId);

  // Polite SR announcement for a successful save. A LOCAL `role="status"`
  // (polite) region — NOT the shared `useSrAnnouncement`, whose `regions`
  // also renders an `aria-live="assertive"` `role="alert"` div that would
  // collide with the inline save-error `role="alert"` (two alerts break the
  // editor's single-alert contract). One polite region is all the save path
  // needs.
  const [announcement, setAnnouncement] = React.useState('');

  // Unmount guard (codex MEDIUM). `handleSave` awaits `mutateAsync`; if the
  // user cancels or navigates away mid-save, the editor unmounts before the
  // await resolves. Without this ref the success continuation would run
  // `setAnnouncement(...)` on an unmounted tree (React `act()` warning) and
  // fire a stale `onDone()`. The continuation short-circuits when unmounted.
  const isMountedRef = React.useRef(true);
  React.useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [notes, setNotes] = React.useState<Map<string, string>>(
    () => new Map(),
  );
  const [search, setSearch] = React.useState('');

  // Seed the draft ONCE from the current assignment when it first becomes
  // ready. A guard ref prevents re-seeding on later renders (which would
  // clobber the user's in-progress toggles).
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (seededRef.current) return;
    if (current.status !== 'ready') return;
    const nextIds = new Set<string>();
    const nextNotes = new Map<string, string>();
    for (const group of current.data) {
      for (const item of group.amenities) {
        nextIds.add(item.amenityId);
        if (item.note !== null && item.note !== undefined) {
          nextNotes.set(item.amenityId, item.note);
        }
      }
    }
    setSelectedIds(nextIds);
    setNotes(nextNotes);
    seededRef.current = true;
  }, [current]);

  const handleToggle = React.useCallback((amenityId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(amenityId)) {
        next.delete(amenityId);
      } else {
        next.add(amenityId);
      }
      return next;
    });
  }, []);

  const handleNoteChange = React.useCallback(
    (amenityId: string, note: string) => {
      setNotes((prev) => {
        const next = new Map(prev);
        next.set(amenityId, note);
        return next;
      });
    },
    [],
  );

  const items = React.useMemo(
    () => buildItems(selectedIds, notes),
    [selectedIds, notes],
  );

  // Shared-validator gate — the button is disabled unless the draft parses
  // clean against the single source of truth (CLAUDE.md FE §4).
  const draftValid = React.useMemo(
    () => setPropertyAmenitiesSchema.safeParse({ items }).success,
    [items],
  );

  const isPending = setAmenities.status === 'pending';

  const handleSave = React.useCallback(async () => {
    try {
      await setAmenities.mutateAsync(items);
      // The editor may have unmounted while the mutation was in flight (Cancel
      // is disabled during pending, but a route change / parent unmount still
      // can) — skip the success continuation so no state update or side-effect
      // lands on an unmounted tree.
      if (!isMountedRef.current) return;
      setAnnouncement('Amenities saved');
      onDone();
    } catch {
      // errorKind drives the inline role="alert"; the editor stays open.
    }
  }, [items, onDone, setAmenities]);

  // The fetch state drives the BODY, but the footer (Cancel + Save) is ALWAYS
  // rendered so the user can always back out — even while the catalog / current
  // assignment is still loading or errored. Save is enabled only once both the
  // catalog and the current assignment are ready (and the draft is valid).
  const ready = catalog.status === 'ready' && current.status === 'ready';
  const noCatalog = ready && catalog.data.length === 0;

  // Filter chips by label (case-insensitive) across every catalog group.
  const query = search.trim().toLowerCase();
  const filteredGroups =
    !ready || query.length === 0
      ? (ready ? catalog.data : [])
      : catalog.data
          .map((group) => ({
            category: group.category,
            amenities: group.amenities.filter((a) =>
              a.label.toLowerCase().includes(query),
            ),
          }))
          .filter((group) => group.amenities.length > 0);

  const errorMessage =
    setAmenities.errorKind !== null
      ? ERROR_COPY[setAmenities.errorKind]
      : null;

  let body: React.ReactNode;
  if (catalog.status === 'loading' || current.status === 'loading') {
    body = (
      <div
        role="status"
        className="py-8 text-center text-sm text-[color:var(--fg-secondary)]"
      >
        Loading amenities…
      </div>
    );
  } else if (catalog.status === 'forbidden' || current.status === 'forbidden') {
    body = (
      <div className="py-8 text-center text-sm text-[color:var(--fg-secondary)]">
        You do not have permission to edit amenities for this property.
      </div>
    );
  } else if (current.status === 'not-found') {
    body = (
      <div className="py-8 text-center text-sm text-[color:var(--fg-secondary)]">
        This property was not found. It may have been removed.
      </div>
    );
  } else if (catalog.status === 'error' || current.status === 'error') {
    body = (
      <div className="py-8 text-center text-sm text-[color:var(--fg-secondary)]">
        Something went wrong while loading amenities.
      </div>
    );
  } else if (noCatalog) {
    body = (
      <div className="rounded-2xl border border-dashed border-[var(--border-default)] bg-[var(--bg-sunken)] px-6 py-10 text-center text-sm text-[color:var(--fg-secondary)]">
        No amenities available in the catalog yet.
      </div>
    );
  } else {
    body = (
      <>
        <TextField
          label="Search amenities"
          type="text"
          icon="search"
          placeholder="Search amenities…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="flex flex-col gap-6">
          {filteredGroups.map((group) => (
            <AmenityCategoryGroup
              key={group.category}
              category={group.category}
              amenities={group.amenities}
              selectedIds={selectedIds}
              notes={notes}
              onToggle={handleToggle}
              onNoteChange={handleNoteChange}
            />
          ))}
        </div>
      </>
    );
  }

  const canSave = ready && !noCatalog && !isPending && draftValid;

  return (
    <div className="flex flex-col gap-5">
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {body}

      {errorMessage !== null ? (
        <div
          role="alert"
          className="rounded-lg p-3 text-sm text-[color:var(--fg-danger-on-tint)]"
          style={{
            background:
              'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
            border:
              '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
          }}
        >
          {errorMessage}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" disabled={isPending} onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canSave}
          aria-busy={isPending}
          onClick={() => {
            void handleSave();
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
